
import React from 'react';
import { ArrowRight, Zap } from 'lucide-react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: string;
  isMobile?: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  delay,
  isMobile = false,
}) => {
  return (
    <div 
      className="group relative flex flex-col h-full rounded-2xl animate-fade-in-up bg-white border border-gray-100 overflow-hidden transition-all duration-500 hover:shadow-lg w-full md:w-full mx-auto px-0 sm:px-0"
      style={{ 
        animationDelay: delay,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        maxWidth: "100%", // Ensure it takes full width on mobile
      }}
    >
      {/* Card Background Effects */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: "linear-gradient(225deg, rgba(254, 230, 216, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%)" }}
      />
      
      {/* Card Accent Border */}
      <div 
        className="absolute left-0 top-0 h-full w-[3px] transition-all duration-300 group-hover:h-full"
        style={{ background: "#FEE6D8" }}
      />
      
      {/* Card Content */}
      <div className="p-4 md:p-6 flex flex-col h-full relative z-10">
        {/* Icon Circle with Pulse Effect */}
        <div className="relative mb-3">
          <div 
            className="w-10 h-10 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
            style={{ background: "#FEE6D8" }}
          >
            {/* Inner Glow Effect */}
            <div
              className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"
              style={{ 
                boxShadow: `0 0 20px 5px rgba(254, 230, 216, 0.6)`,
                background: 'transparent'
              }}
            />
            
            {/* Icon */}
            <div className="text-[#003a70] transition-transform duration-500 group-hover:scale-110">
              {icon}
            </div>
          </div>
          
          {/* Decorative Element */}
          <div 
            className="absolute w-3 h-3 rounded-full -right-1 -top-1 opacity-0 group-hover:opacity-100 transition-all duration-500"
            style={{ background: "#003a70" }}
          >
            <div 
              className="absolute inset-0 rounded-full animate-pulse"
              style={{ boxShadow: `0 0 10px 2px rgba(0, 58, 112, 0.4)` }}
            />
          </div>
        </div>
        
        {/* Card Title with Special Effect */}
        <div className="mb-2 md:mb-3 flex items-center">
          <h3 className="text-sm md:text-base lg:text-xl font-semibold text-brand-dark group-hover:text-[#003a70] transition-colors duration-300 line-clamp-2">
            {title}
          </h3>
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-[-10px] group-hover:translate-x-0">
            <Zap size={isMobile ? 12 : 16} className="text-[#003a70]" />
          </div>
        </div>
        
        {/* Card Description */}
        <p className="text-xs md:text-sm text-brand-gray leading-relaxed flex-grow line-clamp-4 md:line-clamp-none">
          {description}
        </p>
        
        {/* Interactive "Learn More" Element - Hidden on small mobile, visible on tablet and up */}
        <div className="mt-3 pt-1 md:pt-2 flex items-center text-xs font-medium text-[#003a70] opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-2 group-hover:translate-y-0 hidden md:flex">
          <span>Learn more</span>
          <ArrowRight size={14} className="ml-1 transform group-hover:translate-x-1 transition-transform duration-300" />
        </div>
      </div>
    </div>
  );
};

export default FeatureCard;
